import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Forward to Sentry (if configured)
        try {
            Sentry.captureException(error, {
                contexts: { react: { componentStack: errorInfo.componentStack } },
            });
        } catch {
            // Never throw from error boundary logging
        }

        // Silently report to backend without exposing details to the user
        try {
            fetch('/api/client-error', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: error.message,
                    name: error.name,
                    stack: error.stack,
                    componentStack: errorInfo.componentStack,
                    source: 'admin-panel',
                }),
            }).catch(() => {
                // Swallow network/logging errors – never break the UI
            });
        } catch {
            // Never throw from error boundary logging
        }
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 40, color: '#111827', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif', backgroundColor: '#f9fafb', height: '100vh' }}>
                    <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
                        <h1 style={{ fontSize: '24px', marginBottom: '12px' }}>Something went wrong</h1>
                        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
                            An unexpected error occurred. Please try again. If the problem continues, contact support.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                marginTop: 8,
                                padding: '10px 20px',
                                backgroundColor: '#111827',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '9999px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 600,
                            }}
                        >
                            Reload dashboard
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
